import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Handshake, Loader2, Lock, Languages, Quote, PackageCheck, Info, FileSignature, History as HistoryIcon } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const PURCHASE_KEY = "buyer_seller_purchase_agreement";

// Read-only view of the LINKED pre-deal connection thread. No write/send controls,
// no message duplication — pure read from GET /deals/{id}/predeal-conversation.
export default function PreDealPanel({ deal, onOpenContracts }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [genBusy, setGenBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setDenied(false);
    try {
      const { data } = await api.get(`/deals/${deal.deal_id}/predeal-conversation`);
      setData(data);
    } catch (err) {
      if (err.response?.status === 403) setDenied(true);
      else toast.error(formatApiError(err.response?.data?.detail));
      setData(null);
    } finally { setLoading(false); }
  }, [deal.deal_id]);
  useEffect(() => { load(); }, [load]);

  // One-click: reuse the existing Contract module to create a DRAFT purchase agreement
  // for this deal (which already carries the accepted quote / capacity / Need context).
  // Never creates a second contract system; skips if a purchase agreement already exists.
  const generateDraftContract = async () => {
    setGenBusy(true);
    try {
      const { data: cs } = await api.get(`/deals/${deal.deal_id}/contracts`);
      const existing = (cs.contracts || []).find((c) => c.template_key === PURCHASE_KEY);
      if (existing) {
        toast.info("A draft purchase agreement already exists — opening Contracts.");
        onOpenContracts?.();
        return;
      }
      let key = PURCHASE_KEY;
      try {
        const { data: t } = await api.get("/contract-templates");
        const tmpls = t.templates || [];
        if (!tmpls.some((x) => x.key === PURCHASE_KEY) && tmpls.length) key = tmpls[0].key;
      } catch { /* fall back to the default purchase-agreement key */ }
      await api.post(`/deals/${deal.deal_id}/contracts/generate`, { template_key: key });
      toast.success("DRAFT contract generated from the negotiation — legal review required.");
      onOpenContracts?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not generate the draft contract");
    } finally { setGenBusy(false); }
  };

  if (loading) {
    return <div className="mt-6 border-t border-slate-200 pt-5 flex justify-center py-6" data-testid="predeal-loading">
      <Loader2 className="animate-spin text-slate-400" /></div>;
  }

  if (denied) {
    return (
      <div className="mt-6 border-t border-slate-200 pt-5" data-testid="predeal-panel">
        <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500 flex items-center gap-2" data-testid="predeal-denied">
          <Lock size={15} /> You are not authorized to view this pre-deal conversation.
        </div>
      </div>
    );
  }

  if (!data?.linked) {
    return (
      <div className="mt-6 border-t border-slate-200 pt-5" data-testid="predeal-panel">
        <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><Handshake size={15} /> Pre-Deal conversation</h4>
        <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400" data-testid="predeal-empty">
          This Deal Room was not created from a pre-deal connection, so there is no linked negotiation thread to show.
        </div>
      </div>
    );
  }

  const cap = data.capacity_confirmation;
  const quotes = data.quotes || [];
  const messages = data.messages || [];

  return (
    <div className="mt-6 border-t border-slate-200 pt-5" data-testid="predeal-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Handshake size={15} /> Pre-Deal conversation</h4>
        <div className="flex items-center gap-2">
          <button data-testid="predeal-generate-contract-btn" onClick={generateDraftContract} disabled={genBusy}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#2563EB] px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 transition-colors disabled:opacity-60">
            {genBusy ? <Loader2 size={13} className="animate-spin" /> : <FileSignature size={13} />} Generate Draft Contract from Negotiation
          </button>
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-500" data-testid="predeal-readonly-badge">
            Read-only · carried from the connection
          </span>
        </div>
      </div>

      {/* Capacity confirmation */}
      {cap ? (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="predeal-capacity">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
            <PackageCheck size={15} />
            {cap.business_confirmed || cap.source === "business_confirmed" ? "Business-confirmed capacity" : "Capacity (not business-confirmed)"}
          </div>
          <p className="mt-1 text-sm text-emerald-900">
            <strong>{cap.quantity} {cap.unit}</strong>
            {cap.availability_timeframe && <> · available {cap.availability_timeframe}</>}
            {cap.confirming_authority && <> · confirmed by {cap.confirming_authority}</>}
          </p>
          {cap.notes && <p className="mt-0.5 text-xs text-emerald-800">Notes / constraints: {cap.notes}</p>}
          {cap.need_id && <p className="mt-0.5 text-[11px] text-emerald-700">Related Need: {cap.need_id}</p>}
          {Array.isArray(cap.revisions) && cap.revisions.length > 0 && (
            <div className="mt-3 border-t border-emerald-200 pt-2" data-testid="predeal-capacity-history">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700"><HistoryIcon size={12} /> Capacity revision history (read-only)</p>
              <div className="mt-1.5 space-y-1.5">
                {[{ ...cap, _current: true }, ...cap.revisions.slice().reverse()].map((r, i) => (
                  <div key={i} data-testid={`predeal-capacity-rev-${i}`}
                    className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded px-2 py-1 text-[11px] ${r._current ? "bg-emerald-100 text-emerald-900" : "bg-white/60 text-slate-600"}`}>
                    {r._current
                      ? <span className="rounded bg-emerald-600 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">Current</span>
                      : <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[9px] font-bold uppercase text-slate-500">Prior</span>}
                    <span className="font-semibold">{r.quantity} {r.unit}</span>
                    {r.availability_timeframe && <span>· {r.availability_timeframe}</span>}
                    {r.confirming_authority && <span>· by {r.confirming_authority}</span>}
                    {r.confirmed_at && <span className="text-slate-400">· {String(r.confirmed_at).slice(0, 16).replace("T", " ")}</span>}
                    {r.notes && <span className="w-full text-slate-400">notes: {r.notes}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-center gap-2" data-testid="predeal-capacity-none">
          <Info size={14} /> No business-confirmed capacity was recorded before this deal was opened.
        </div>
      )}

      {/* Negotiated quotes / terms */}
      {quotes.length > 0 && (
        <div className="mb-4" data-testid="predeal-quotes">
          <h5 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Quote size={13} /> Negotiated quotes</h5>
          <div className="space-y-2">
            {quotes.map((q) => (
              <div key={q.quote_id} data-testid={`predeal-quote-${q.quote_id}`}
                className={`rounded-md border p-2.5 text-sm ${q.status === "accepted" ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-800">
                    {q.quantity} {q.unit} @ {q.currency || "USD"} {q.unit_price}
                    {q.incoterm && <span className="ml-1 text-slate-500">({q.incoterm})</span>}
                  </span>
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${q.status === "accepted" ? "bg-emerald-200 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
                    {(q.status || "proposed").replace(/_/g, " ")}
                  </span>
                </div>
                {q.delivery_date && <p className="mt-0.5 text-[11px] text-slate-500">Delivery: {q.delivery_date}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Conversation thread (original + translated) */}
      <h5 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Conversation history</h5>
      <div className="max-h-72 space-y-2 overflow-y-auto rounded-lg border border-slate-200 p-3" data-testid="predeal-messages">
        {messages.length === 0 && <p className="text-sm text-slate-400">No messages were exchanged on this connection.</p>}
        {messages.map((m) => {
          const showTranslated = m.translated && m.translated !== m.body;
          return (
            <div key={m.message_id} data-testid={`predeal-msg-${m.message_id}`} className="rounded-md bg-slate-50 p-2.5 text-sm">
              <div className="mb-0.5 flex items-center justify-between text-[11px] text-slate-400">
                <span>{m.author_role || "participant"}</span>
                <span>{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</span>
              </div>
              {showTranslated && (
                <p className="text-slate-800 flex items-start gap-1.5" data-testid={`predeal-msg-translated-${m.message_id}`}>
                  <Languages size={13} className="mt-0.5 shrink-0 text-blue-500" />
                  <span>{m.translated}</span>
                </p>
              )}
              <p className={showTranslated ? "mt-1 border-l-2 border-slate-200 pl-2 text-[12px] text-slate-500" : "text-slate-700"}
                data-testid={`predeal-msg-original-${m.message_id}`}>
                {showTranslated && <span className="mr-1 font-medium">Original ({m.original_lang || "en"}):</span>}
                {m.body}
              </p>
            </div>
          );
        })}
      </div>
      <p className="mt-1.5 text-[11px] text-slate-400">
        This is the original negotiation thread from the linked connection. Translated text is advisory — original commercial terms are preserved. Continue the conversation in the deal channels.
      </p>
    </div>
  );
}
